// src/pages/task/TaskCreatePage.tsx
// Writing down a task.
//
// The form itself is `TaskFormComposer` — the same component the edit page
// renders, so the two cannot drift. This page owns only what is true about
// *creating*: where you are, where the templates are, the draft that survives
// a mis-click, and a Create button.
//
// The default parent is "no parent" — a real, chosen state that sends no
// `parent` at all. `ResolveTaskContext` still needs somewhere to resolve
// against, so the tenant is sent as the *resolution* scope while the created
// task carries nothing. The two are different questions and stay different in
// the payload.
//
// Fast path: title + Create, reachable in seconds, ⌘/Ctrl+Enter submits.
// Everything typed is mirrored into a per-scope sessionStorage draft, so an
// accidental navigation costs nothing.

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';

import { Button } from '@react/components';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import {
  FORM_ICONS,
  Icon,
  PARENT_KINDS,
  TaskFormComposer,
  TemplateMenu,
  useTaskForm,
} from '@/features/task/form';
import type { ParentKind } from '@/features/task/form';

/** DOM id of the title field — also the focus target when the title is invalid. */
const TITLE_FIELD_ID = 'task-create-title';

export const TaskCreatePage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();

  // A deep link may name the parent; nothing else does. The default is "no
  // parent" — a real, chosen state that sends no `parent` at all.
  const urlParentId = searchParams.get('parent_id') || searchParams.get('project') || '';
  const urlParentType = searchParams.get('parent_type') || (urlParentId ? 'project' : 'none');
  const [initialParent] = useState(() => ({
    kind: ((PARENT_KINDS as readonly string[]).includes(urlParentType)
      ? urlParentType
      : 'entity') as ParentKind,
    id: urlParentId,
    typeName: (PARENT_KINDS as readonly string[]).includes(urlParentType) ? '' : urlParentType,
  }));
  const [urlTemplateId] = useState(() => searchParams.get('template'));

  // Everything about *what a task is* comes from the shared hook — the same
  // one the quick-create drawer and the edit page call, so no surface can grow
  // its own idea of what a task has.
  const f = useTaskForm({
    initialParent,
    templateId: urlTemplateId,
    titleFieldId: TITLE_FIELD_ID,
    // On the full page, creating a task means going to it: this surface is for
    // considered work, and the thing you considered is now worth opening.
    onCreated: (_created: unknown, createdId: string) =>
      navigate(createdId ? `/tasks/${createdId}` : '/tasks'),
  });

  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // The action bar, pinned so Create is always one click away — and saying what
  // is still missing before it is pressed.
  const actionBar = (
    <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <p className="min-w-0 flex-1 text-xs" aria-live="polite">
        {f.missingRequired.length > 0 ? (
          <span className="text-destructive">
            {t('task.createForm.requiredRemaining', {
              count: f.missingRequired.length,
              fields: f.missingRequired.join(', '),
            })}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('task.submitHint')}</span>
        )}
      </p>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => (f.isDirty ? setConfirmDiscard(true) : navigate('/tasks'))}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!f.canSubmit} isLoading={f.isSubmitting}>
          {t('task.create')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-5xl pb-24">
      {/* ── Heading row: the navbar breadcrumb already says where you are;
             what stays here is the one way in that is not "start typing" ── */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <h1 className="sr-only">{t('task.create')}</h1>

        <TemplateMenu
          entries={f.templateEntries}
          value={f.selectedTemplateId}
          onSelect={f.applyTemplate}
          parentLabel={f.parentDisplayName}
          createHref={`/task-templates/new${f.parentId ? `?project=${f.parentId}` : ''}`}
        />
      </div>

      {/* A restored draft explains why the fields are not empty, so it sits
          with them rather than at the bottom of the page. */}
      {f.draftRestored && (
        <div role="status" className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Icon d={FORM_ICONS.restore} className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-muted-foreground">{t('task.createForm.draftRestored')}</span>
          <button
            type="button"
            onClick={f.discardDraft}
            className="rounded-sm font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('task.createForm.discardDraft')}
          </button>
          <button
            type="button"
            onClick={() => f.setDraftRestored(false)}
            aria-label={t('common.close')}
            className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon d={FORM_ICONS.x} className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <TaskFormComposer form={f} idPrefix="task-create" actions={actionBar} />

      <ConfirmDialog
        open={confirmDiscard}
        title={t('task.createForm.discardTitle')}
        description={t('task.createForm.discardDescription')}
        confirmLabel={t('task.createForm.discardConfirm')}
        cancelLabel={t('task.createForm.discardCancel')}
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          f.discardDraft();
          navigate('/tasks');
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
});

export default TaskCreatePage;
